import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import { useApiFetch } from '../../shared/api'
import type { EndedHuntDto } from './useEndedHunts'
import type { DraftHuntDto } from './useDraftHunts'
import type { Hunt } from '@wehunt/ui'
import { rsvpFromInviteStatus } from './upcomingHuntToHunt'

export interface GroupUpcomingHuntDto {
  id: string
  huntName: string
  groupId: string
  groupName: string
  startTime: string
  endTime: string | null
  participantCount: number
  status: string
  huntableGames: string[]
  isOrganizer: boolean
  isParticipant: boolean
  invitedBy: string | null
  inviteStatus: string | null
  /** When the caller's invitation was last sent; null or absent when there is none. */
  invitedAt?: string | null
}

export interface GroupHuntsResponse {
  upcoming: GroupUpcomingHuntDto[]
  drafts: DraftHuntDto[]
  ended: EndedHuntDto[]
  /** Live hunts (ongoing/paused). Same shape as {@link ended}. */
  ongoing: EndedHuntDto[]
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

export function groupUpcomingDtoToHunt(dto: GroupUpcomingHuntDto): Hunt {
  const start = new Date(dto.startTime)
  const end = dto.endTime ? new Date(dto.endTime) : start
  const quarry = dto.huntableGames.length > 0 ? dto.huntableGames.join(', ') : '—'
  return {
    id: dto.id,
    date: `${start.getFullYear()}-${pad2(start.getMonth() + 1)}-${pad2(start.getDate())}`,
    start: `${pad2(start.getHours())}:${pad2(start.getMinutes())}`,
    end: `${pad2(end.getHours())}:${pad2(end.getMinutes())}`,
    team: dto.groupName,
    teamId: dto.groupId,
    quarry,
    location: dto.huntName,
    participants: dto.participantCount,
    dogs: [],
    status: 'upcoming',
    invitedBy: dto.invitedBy ?? undefined,
    invitedAt: dto.invitedAt ?? undefined,
    myRsvp: rsvpFromInviteStatus(dto.inviteStatus),
    isParticipant: dto.isParticipant,
  }
}

export function useGroupHunts(groupId: string | undefined) {
  const call = useApiFetch()
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  return useQuery({
    queryKey: ['clubhouse', 'groups', groupId, 'hunts', uid],
    queryFn: () => call<GroupHuntsResponse>(`/clubhouse/groups/${groupId}/hunts`),
    enabled: !!uid && !!groupId,
    staleTime: 60 * 1000,
  })
}
